import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import MessageToast from '../MessageToast';
import StyledSection from '../styles/StyledSection';
import { StyledHeading } from '../styles/styles';
import { Palette } from 'Palette';
import InvitationEmailComposePanel from './InvitationEmailComposePanel';
import { useGenerateInvitationEmail } from './useGenerateInvitationEmail';

const GenerateInvitationEmail: React.FC<{ tone?: keyof typeof Palette }> = ({ tone = 'tone1' }) => {
  const {
    loading,
    sending,
    templateData,
    templateParts,
    templateHtml,
    customHtml,
    subject,
    setSubject,
    fromEmail,
    setFromEmail,
    message,
    emailPreviewRef,
    iframeContainerRef,
    showSpeechBubble,
    elementPosition,
    handleEnterEditMode,
    handleContentChange,
    handleElementPosition,
    handleHideSpeechBubble,
    handleSend,
    navigateToContact,
    dismissMessage,
  } = useGenerateInvitationEmail();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!templateData || !templateParts) {
    return (
      <>
        <StyledSection tone={tone}>
          <StyledHeading tone={tone}>Error</StyledHeading>
          <Typography>Failed to load email template</Typography>
        </StyledSection>
        {message && (
          <MessageToast
            type={message.type}
            message={message.text}
            open={true}
            onClose={dismissMessage}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Box sx={{ py: 3 }}>
        <Stack spacing={3}>
          <StyledSection tone={tone} noTopPadding>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={navigateToContact}
                size="small"
                aria-label="Back"
                edge="start"
                sx={{ color: Palette.default.text }}
              >
                <ArrowBackIcon />
              </IconButton>
              <StyledHeading tone={tone} fullTopSpacing sx={{ mb: 0 }}>
                Compose Invitation Email
              </StyledHeading>
            </Box>
          </StyledSection>

          <StyledSection tone={tone}>
            <InvitationEmailComposePanel
              tone={tone}
              templateData={templateData}
              templateHtml={templateHtml}
              fromEmail={fromEmail}
              onFromEmailChange={setFromEmail}
              subject={subject}
              onSubjectChange={setSubject}
              customHtml={customHtml}
              emailPreviewRef={emailPreviewRef}
              iframeContainerRef={iframeContainerRef}
              showSpeechBubble={showSpeechBubble}
              elementPosition={elementPosition}
              sending={sending}
              onContentChange={handleContentChange}
              onEnterEditMode={handleEnterEditMode}
              onElementPosition={handleElementPosition}
              onHideSpeechBubble={handleHideSpeechBubble}
              onCancel={navigateToContact}
              onSend={handleSend}
            />
          </StyledSection>
        </Stack>
      </Box>

      {message && (
        <MessageToast
          type={message.type}
          message={message.text}
          open={true}
          onClose={dismissMessage}
        />
      )}
    </>
  );
};

export default GenerateInvitationEmail;
